import { Alert } from '@shared/components';
import type { PeriodStatementBase } from '../../../../types/dealerFundStatementPeriod.types';
import { balanceUnsplitShares } from '../../../../utils/balanceUnsplit';
import { NET_BASIS_FOOTNOTE, balanceUnsplitNotice, roundingFootnote } from '../../../../utils/fundStatementLabels';
import type { StatementLayout } from '../../../../utils/statementBlocks';
import { BalanceRows } from './BalanceRows';
import { MemoRow } from './MemoRow';
import { STATEMENT_MIN_WIDTH } from './statementGrid';
import { StatementBlockSection, type RenderLine } from './StatementBlockSection';

export type PeriodStatementFigures = Pick<
    PeriodStatementBase,
    | 'openingBalance'
    | 'closingBalance'
    | 'openingBalanceNet'
    | 'closingBalanceNet'
    | 'unsplit'
    | 'balanceUnsplit'
    | 'roundingMode'
    | 'currency'
>;

interface Props {
    statement: PeriodStatementFigures;
    layout: StatementLayout;
    renderLine?: RenderLine;
}

export function PeriodStatementBody({ statement, layout, renderLine }: Props) {
    const { currency } = statement;
    const shares = balanceUnsplitShares(statement);
    return (
        <div className="overflow-x-auto" role="region" aria-label="Fund statement figures" tabIndex={0}>
            <div className={`${STATEMENT_MIN_WIDTH} space-y-4`}>
                <BalanceRows
                    label="Opening Balance of Fund (Net)"
                    net={statement.openingBalanceNet}
                    gross={statement.openingBalance}
                    unsplit={shares.opening}
                    currency={currency}
                />
                {layout.blocks.map((block) => (
                    <StatementBlockSection key={block.key} block={block} currency={currency} renderLine={renderLine} />
                ))}
                {layout.unassigned && <MemoRow memo={layout.unassigned} currency={currency} />}
                <BalanceRows
                    label="Closing Balance of Fund (Net)"
                    net={statement.closingBalanceNet}
                    gross={statement.closingBalance}
                    unsplit={shares.closing}
                    currency={currency}
                    emphasised
                />
                {shares.closing.postings > 0 && (
                    <Alert variant="warning" role="note">
                        {balanceUnsplitNotice(shares.closing.postings, shares.closing.gross, currency)}
                    </Alert>
                )}
                <p className="text-xs text-mp-muted">{NET_BASIS_FOOTNOTE}</p>
                <p className="text-xs text-mp-muted">{roundingFootnote(statement.roundingMode)}</p>
            </div>
        </div>
    );
}
